import { reset } from '@/lib/analytics';
import { supabase } from '@/lib/supabase';
import { Session, User } from '@supabase/supabase-js';
import Constants from 'expo-constants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';

const getSupabaseConfig = () => {
  if (Platform.OS === 'web') {
    return {
      supabaseUrl: process.env.EXPO_PUBLIC_SUPABASE_URL ?? '',
      supabaseAnonKey: process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ?? '',
    };
  }
  const extra = (Constants.expoConfig?.extra ?? {}) as { supabaseUrl?: string; supabaseAnonKey?: string };
  return { supabaseUrl: extra.supabaseUrl ?? '', supabaseAnonKey: extra.supabaseAnonKey ?? '' };
};

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Validate URL format
  const isValidUrl = (url: string): boolean => {
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  useEffect(() => {
    console.log('[useAuth] useEffect triggered - Initializing auth state...');

    // Check if Supabase is configured (from app.config extra)
    const { supabaseUrl, supabaseAnonKey } = getSupabaseConfig();

    console.log('[useAuth] Environment check:', {
      hasUrl: !!supabaseUrl,
      hasKey: !!supabaseAnonKey,
      urlPreview: supabaseUrl ? `${supabaseUrl.substring(0, 30)}...` : 'empty',
      isPlaceholder: supabaseUrl === 'https://placeholder.supabase.co',
      isValidUrl: isValidUrl(supabaseUrl),
    });

    if (!supabaseUrl || !supabaseAnonKey || supabaseUrl === 'https://placeholder.supabase.co' || !isValidUrl(supabaseUrl)) {
      console.warn('[useAuth] Supabase not configured or invalid URL, treating as unauthenticated');
      console.log('[useAuth] Setting state: session=null, user=null, loading=false');
      setSession(null);
      setUser(null);
      setLoading(false);
      return;
    }

    // Check if supabase client is available
    if (!supabase) {
      console.warn('[useAuth] Supabase client is null, treating as unauthenticated');
      setSession(null);
      setUser(null);
      setLoading(false);
      return;
    }

    // Get initial session with error handling
    console.log('[useAuth] Calling supabase.auth.getSession()...');
    try {
      const sessionPromise = supabase.auth.getSession();
      if (!sessionPromise || typeof sessionPromise.then !== 'function') {
        console.error('[useAuth] getSession() did not return a promise');
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }

      sessionPromise.then(({ data: { session }, error }) => {
        console.log('[useAuth] getSession() response received');
        if (error) {
          console.error('[useAuth] Error getting session:', error);
          console.log('[useAuth] Setting state: session=null, user=null, loading=false (due to error)');
          setSession(null);
          setUser(null);
        } else {
          console.log('[useAuth] Initial session result:', {
            hasSession: !!session,
            hasUser: !!session?.user,
            userId: session?.user?.id,
            email: session?.user?.email,
          });
          setSession(session);
          setUser(session?.user ?? null);
        }
        console.log('[useAuth] Setting loading=false');
        setLoading(false);
      }).catch((error) => {
        console.error('[useAuth] Exception getting session:', error);
        console.log('[useAuth] Setting state: session=null, user=null, loading=false (due to exception)');
        setSession(null);
        setUser(null);
        setLoading(false);
      });
    } catch (error) {
      console.error('[useAuth] Error initializing auth:', error);
      console.log('[useAuth] Setting state: session=null, user=null, loading=false (due to catch)');
      setSession(null);
      setUser(null);
      setLoading(false);
      return;
    }

    // Listen for auth changes with error handling
    console.log('[useAuth] Setting up auth state change listener...');
    try {
      if (!supabase) {
        console.warn('[useAuth] Supabase client is null, cannot set up auth listener');
        return;
      }

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((event, session) => {
        console.log('[useAuth] Auth state changed event:', {
          event,
          hasSession: !!session,
          hasUser: !!session?.user,
          userId: session?.user?.id,
          email: session?.user?.email,
        });

        // Only update state if values actually changed to prevent unnecessary re-renders
        setSession((prevSession) => {
          // Compare session IDs to avoid unnecessary updates
          if (prevSession?.access_token === session?.access_token && prevSession?.user?.id === session?.user?.id) {
            return prevSession;
          }
          return session;
        });

        setUser((prevUser) => {
          const newUser = session?.user ?? null;
          // Compare user IDs to avoid unnecessary updates
          if (prevUser?.id === newUser?.id) {
            return prevUser;
          }
          return newUser;
        });

        setLoading((prevLoading) => {
          if (!prevLoading) return prevLoading;
          console.log('[useAuth] Setting loading=false after auth state change');
          return false;
        });
      });
      console.log('[useAuth] Auth state change listener set up successfully');

      return () => {
        try {
          subscription.unsubscribe();
        } catch (error) {
          console.error('[useAuth] Error unsubscribing:', error);
        }
      };
    } catch (error) {
      console.error('[useAuth] Error setting up auth listener:', error);
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    console.log('[useAuth] signOut called');
    if (!supabase) {
      console.warn('[useAuth] Cannot sign out - Supabase client is null');
      setSession(null);
      setUser(null);
      reset();
      return;
    }

    const { supabaseUrl } = getSupabaseConfig();
    if (!supabaseUrl || supabaseUrl === 'https://placeholder.supabase.co') {
      console.warn('[useAuth] Using placeholder client, clearing local state only');
      setSession(null);
      setUser(null);
      reset();
      return;
    }

    try {
      console.log('[useAuth] Calling supabase.auth.signOut()...');

      // Check if signOut method exists and returns a promise
      if (!supabase.auth || typeof supabase.auth.signOut !== 'function') {
        console.error('[useAuth] signOut method not available');
        // Clear local state anyway
        setSession(null);
        setUser(null);
        return;
      }

      const signOutPromise = supabase.auth.signOut();

      // Check if it's actually a promise
      if (!signOutPromise || typeof signOutPromise.then !== 'function') {
        console.error('[useAuth] signOut did not return a promise');
        // Clear local state anyway
        setSession(null);
        setUser(null);
        return;
      }

      const { error } = await signOutPromise;

      if (error) {
        console.error('[useAuth] Error signing out:', error);
        // Still clear local state on error
        setSession(null);
        setUser(null);
        throw error;
      }

      console.log('[useAuth] ✅ Sign out successful');
      reset();
      // The auth state change listener will automatically update the state
    } catch (error) {
      console.error('[useAuth] Exception during sign out:', error);
      // Clear local state even on error
      setSession(null);
      setUser(null);
      reset();
      throw error;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    console.log('[useAuth] refreshUser called');
    if (!supabase) {
      console.warn('[useAuth] Cannot refresh user - Supabase client is null');
      return;
    }

    try {
      const { data: { user: refreshedUser }, error } = await supabase.auth.getUser();
      if (error) {
        console.error('[useAuth] Error refreshing user:', error);
        // Don't clear existing user on error - just log and return
        return;
      }

      // Only update if we got a valid user
      if (refreshedUser) {
        console.log('[useAuth] User refreshed:', { userId: refreshedUser.id, email: refreshedUser.email });
        setUser(refreshedUser);
        // Also get the session to ensure it's in sync
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession) {
          setSession(currentSession);
        }
      } else {
        // If no user returned, don't clear existing user - might be a temporary issue
        console.warn('[useAuth] No user returned from refresh, keeping existing user');
      }
    } catch (error) {
      console.error('[useAuth] Exception refreshing user:', error);
      // Don't clear user on exception - keep existing state
    }
  }, []);

  const isAuthenticated = useMemo(() => !!user, [user]);

  // Only log when values actually change to reduce console noise
  useEffect(() => {
    console.log('[useAuth] State updated:', { loading, isAuthenticated, hasUser: !!user, hasSession: !!session });
  }, [loading, isAuthenticated, user, session]);

  return useMemo(
    () => ({
      session,
      user,
      loading,
      isAuthenticated,
      signOut,
      refreshUser,
    }),
    [session, user, loading, isAuthenticated, signOut, refreshUser]
  );
}
